import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Text from '@components/RTE/Text';
import {
  TextInput,
  TextArea,
  EmailInput,
  CustomSelect,
  Checkbox,
  SubmitButton,
  FacilitySearch,
  ZipCodeInput
} from '../form-elements';
import ruleSet from './ruleSet';
import { States, Credentials, Specialties, AssistanceOptions, AffairsFacilityOptions } from './formSelectOptions';
import SuccessMessage from '../successMessage';
import Container from '@components/Container';
import FORM_CONFIG from './formConfig';
import { setupFacilityListener } from './facilityHandlers';
import { getFieldRules } from '../utils/formValidation';
import useSiteMetadata from '@hooks/useSiteMetadata';
import { getFormattedCredentials, getFormattedSpeciality } from '../utils/payload-formatting';
import GTM from '@utils/GTM';


/**
 * Sets up an observer to monitor form section errors
 * @param {string} sectionId - The ID of the section to observe
 * @param {Function} setHasErrors - State setter function to update error status
 * @returns {Function} Cleanup function to disconnect the observer
 */
const setupSectionErrorObserver = (sectionId, setHasErrors) => {
  const checkSectionErrors = () => {
    const section = document.getElementById(sectionId);
    if (section) {
      const errorElements = section.getElementsByClassName('error-active');
      setHasErrors(errorElements.length > 0);
    }
  };

  // Initial check
  checkSectionErrors();

  // Create observer to watch for changes in error-active classes
  const observer = new MutationObserver(checkSectionErrors);
  const section = document.getElementById(sectionId);

  if (section) {
    observer.observe(section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  return () => observer.disconnect();
};

const RequestARepresentativeForm = ({
  className,
  onSubmitSuccess,
  successBlock,
  formIntroductionHeader,
  formIntroductionFooter
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasPISectionErrors, setHasPISectionErrors] = useState(false);
  const [hasClinicalSectionErrors, setHasClinicalSectionErrors] = useState(false);
  const stateSelectRef = useRef(null);
  const { apiUrl } = useSiteMetadata();

  const methods = useForm({
    defaultValues: FORM_CONFIG.defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const {
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    trigger,
	  formState: { errors, isSubmitSuccessful, touchedFields, dirtyFields }
  } = methods;

  // Watch credential field for changes
  const selectedCredential = watch('credential');

  // Revalidate NPI when credential changes
  useEffect(() => {
    if (selectedCredential) {
      trigger('npiNumberCreds');
    }
  }, [selectedCredential, trigger]);

  useEffect(() => {
    GTM.formDelivery('Connect');
    GTM.formInitiation('Connect');
    GTM.fieldCompletion('Connect');
  }, []);

  // Clear form and errors after successful submission
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      // Dispatch form reset event
      document.dispatchEvent(new Event('form:reset'));
    }
  }, [isSubmitSuccessful, reset]);

  // Listen for facility selection event
  useEffect(() => {
    return setupFacilityListener(setValue, stateSelectRef);
  }, [setValue]);

  // Add useEffect to check for section errors
  useEffect(() => {
    const piObserver = setupSectionErrorObserver(FORM_CONFIG.sections.pi.id, setHasPISectionErrors);
    const clinicalObserver = setupSectionErrorObserver(FORM_CONFIG.sections.clinical.id, setHasClinicalSectionErrors);

    return () => {
      piObserver();
      clinicalObserver();
    };
  }, []);

  // Move all useMemo hooks to the top level
  const credentialOptions = useMemo(() =>
    Object.entries(Credentials).map(([value, label]) => ({
      value,
      label: label.label
    })), []
  );

  const specialtyOptions = useMemo(() =>
    Object.entries(Specialties).map(([value, label]) => ({
      value,
      label
    })), []
  );

  const affairsFacilityOptions = useMemo(() => AffairsFacilityOptions, []);

  const stateOptions = useMemo(() =>
    Object.entries(States).map(([abbr, name]) => ({
      label: name,
      value: abbr
    })), []
  );

  const assistanceOptions = useMemo(() => AssistanceOptions, []);

  const onSubmit = async (data) => {

    const payload = {
      code: "form_hcp_rar",
      first_name: data.firstName,
      middle_name: data.middleName,
      last_name: data.lastName,
      email: data.email,
      credentials: getFormattedCredentials(data.credential),
      speciality: getFormattedSpeciality(data.specialty),
      npi: data.npiNumberCreds,
      facility_name: data.facilitySearch,
      address: data.address,
      city: data.city,
      state: data.state.toLowerCase(),
      phone: data.phoneNumber,
      zip_code: data.zipCode,
      gov_provider: data.affairsFacility === "yes" ? "true" : "false",
      optin_comm: data.optInFutureCommunications,
      hco_entity_id: data.hco_entity_id,
      message: data.message,
    };

    // Here we send the form data to a server
    try {
      const response = await fetch(`${apiUrl}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Error submitting form', response.statusText);
      } else {
        GTM.formSubmission('Connect');
      }
    } catch (error) {
      // TODO: handle error properly and show error state
      console.error('Error submitting form:', error);
    }

    // Call the success callback if provided
    if (onSubmitSuccess) {
      onSubmitSuccess(data);
    }

    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return <SuccessMessage successBlock={successBlock} />;
  }

  // Helper function to get validation rules for this specific form
  const getFormFieldRules = (fieldName) => {
    return getFieldRules(fieldName, ruleSet, {
      credentials: Credentials,
      formKey: 'form_req_rep'
    });
  };

  const getText = (item) => {
    return (
      <Text
        content={item?.text?.raw}
        classOverlay={`${item?.classOverlay?.classOverlay} ${item?.textAlign === 'Center' ? 'mx-auto' : ''}`}
        marginTopDesktop={item?.marginTopDesktop}
        marginBottomDesktop={item?.marginBottomDesktop}
        marginTopMobile={item?.marginTopMobile}
        marginBottomMobile={item?.marginBottomMobile}
        paddingTopDesktop={item?.paddingTopDesktop}
        paddingBottomDesktop={item?.paddingBottomDesktop}
        paddingTopMobile={item?.paddingTopMobile}
        paddingBottomMobile={item?.paddingBottomMobile}
        paddingLeftDesktop={item?.paddingLeftDesktop}
        paddingRightDesktop={item?.paddingRightDesktop}
        paddingLeftMobile={item?.paddingLeftMobile}
        paddingRightMobile={item?.paddingRightMobile}
        maxWidthDesktop={item?.maxWidthDesktop}
        maxWidthMobile={item?.maxWidthMobile}
        textAlign={item?.textAlign}
        textColor={item?.textColor?.textColor}
        references={item?.references}
        style={item?.style}
        nodeStyles={item?.nodeStyles}
        customRenderers={item?.customRenderers}
        customMarks={item?.customMarks}
      />
    );
  };

  return (
    <div>
      {formIntroductionHeader && formIntroductionHeader.length > 0 && (
        <>
          {formIntroductionHeader.map((block, blockIndex) => (
            <Container
              key={`header-block-${blockIndex}`}
              backgroundImage={block.backgroundImage}
              className={`${block.classOverlay?.classOverlay || ''}`}
              type={block.type}
              paddingTopDesktop={block.paddingTopDesktop}
              paddingBottomDesktop={block.paddingBottomDesktop}
              paddingTopMobile={block.paddingTopMobile}
              paddingBottomMobile={block.paddingBottomMobile}
              paddingLeftDesktop={block.paddingLeftDesktop}
              paddingRightDesktop={block.paddingRightDesktop}
              paddingLeftMobile={block.paddingLeftMobile}
              paddingRightMobile={block.paddingRightMobile}

            >
              {block.items?.map((item, index) => (
                <div key={`header-item-${index}`}>
                  {item.__typename === 'ContentfulRichText' ? getText(item) : null}
                </div>
              ))}
            </Container>
          ))}
        </>
      )}
      <FormProvider {...methods}>
        <form
          id={FORM_CONFIG.id}
				  onSubmit={
					  async (e) => {
						  e.preventDefault(); // Prevent default form submission

						  // Run validation on all fields first
						  const isValid = await methods.trigger();

						  // Now errors object should be populated
						  // Track all field validations after validation
						  const fields = Object.keys(FORM_CONFIG.defaultValues);
						  const currentValues = methods.getValues();
						  const currentErrors = methods.formState.errors;

						  fields.forEach(fieldName => {
							  // Only track validation for required fields or fields with values
							  const fieldRules = getFormFieldRules(fieldName);
							  const isRequired = fieldRules?.required || false;
							  const hasValue = !!currentValues[fieldName];
							  const errorValue = currentErrors[fieldName]?.message || 'Valid';

							  if (errorValue && errorValue !== 'Valid') {
								  GTM.fieldValidation('Connect', fieldName, errorValue);
							  }
						  });


						  // Continue with form submission if valid
						  handleSubmit(onSubmit)(e);
					  }
				  }
          className={`max-w-[1204px] mx-4 lg:mx-auto pb-6 flex flex-col gap-12 relative ${className}`}
          aria-label="Connect Form"
          noValidate
        >

          {/* PI Section */}
          <section id={FORM_CONFIG.sections.pi.id} aria-label="Personal Information" className="bg-white py-12 px-6 lg:p-14 rounded-all_48">
            <h2 className="text-p_medium lg:text-h4_medium text-dark_green mb-8 lg:mb-12 text-center" id={FORM_CONFIG.sections.pi.id}>
              {FORM_CONFIG.sections.pi.title}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6"
              role="group"
              aria-labelledby={FORM_CONFIG.sections.pi.id}
            >
              <TextInput
                label="First Name"
                placeholder="First Name*"
                {...register('firstName', getFormFieldRules('firstName'))}
                aria-required="true"
                error={errors.firstName?.message}
                id="firstName"
                maxLength={100}
              />

              <TextInput
                label="Middle Name"
                placeholder="Middle Name (Optional)"
                {...register('middleName', getFormFieldRules('middleName'))}
                aria-required="false"
                error={errors.middleName?.message}
                id="middleName"
                maxLength={100}
              />

              <TextInput
                label="Last Name"
                placeholder="Last Name*"
                {...register('lastName', getFormFieldRules('lastName'))}
                aria-required="true"
                error={errors.lastName?.message}
                id="lastName"
                maxLength={100}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
              role="group"
              aria-labelledby={FORM_CONFIG.sections.pi.id}
            >
              <EmailInput
                label="Email Address"
                placeholder="Email Address*"
                {...register('email', getFormFieldRules('email'))}
                aria-required="true"
                error={errors.email?.message}
                id="email"
                maxLength={255}
              />

              <CustomSelect
                label="Credential"
                placeholder="Credential"
                {...register('credential', getFormFieldRules('credential'))}
                options={credentialOptions}
                error={errors.credential?.message}
                additionalText=" (Choose One)*"
                id="credential"
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
              role="group"
              aria-labelledby={FORM_CONFIG.sections.pi.id}
            >

              <TextInput
                label="NPI"
                placeholder="NPI*"
                {...register('npiNumberCreds', getFormFieldRules('npiNumberCreds'))}
                aria-required="true"
                error={errors.npiNumberCreds?.message}
                id="npiNumberCreds"
                maxLength={10}
              />

              <CustomSelect
                label="Specialty"
                placeholder="Specialty"
                {...register('specialty', getFormFieldRules('specialty'))}
                options={specialtyOptions}
                error={errors.specialty?.message}
                additionalText=" (Choose One)*"
                id="specialty"
              />
            </div>
            <div className="grid grid-cols-1 gap-6">
              <CustomSelect
                label="Do you work at a Department of Defence or Veterans Affairs facility"
                {...register('affairsFacility', getFormFieldRules('affairsFacility'))}
                placeholder="Do you work at a Department of Defence or Veterans Affairs facility"
                options={affairsFacilityOptions}
                error={errors.affairsFacility?.message}
                additionalText=" (Choose One)*"
                id="affairsFacility"
                dropdownHeight="h-[170px]"
              />
            </div>
            <div className="grid grid-cols-1 gap-6 mt-8 lg:mt-12">
              <span className="text-p_footnote text-black/70">
                *Required field
              </span>
              {hasPISectionErrors && (
                <span id={FORM_CONFIG.sections.pi.errorId} className="text-p text-error">
                  *Please fill all the required fields
                </span>
              )}
            </div>
          </section>

          {/* Clinical Info Section */}
          <section id={FORM_CONFIG.sections.clinical.id} className="bg-white py-12 px-6 lg:p-14 rounded-all_48" aria-label="Hospital/Practice/Clinic Information">
            <h2 className="text-p_medium lg:text-h4_medium text-dark_green mb-6 text-center" id={FORM_CONFIG.sections.clinical.id}>
              {FORM_CONFIG.sections.clinical.title}
            </h2>
            <p className="text-[14px] lg:text-p font-gt_walsheim_regular text-black/70 mb-8 text-center">
              Use our <span className="font-bold">search tool</span> to find the facility you are looking for and we will fill out the rest!
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
              role="group"
              aria-labelledby={FORM_CONFIG.sections.clinical.id}
            >
              <input type="hidden" id="hco_entity_id" {...register('hco_entity_id')} />

              <FacilitySearch
                label="Facility Search"
                placeholder="Facility Search*"
                {...register('facilitySearch', getFormFieldRules('facilitySearch'))}
                aria-required="true"
                error={errors.facilitySearch?.message}
                id="facilitySearch"
                maxLength={255}
              />

              <TextInput
                label="Address"
                placeholder="Address*"
                {...register('address', getFormFieldRules('address'))}
                error={errors.address?.message}
                aria-required="true"
                id="address"
                maxLength={255}
              />

              <TextInput
                label="City"
                placeholder="City*"
                {...register('city', getFormFieldRules('city'))}
                error={errors.city?.message}
                aria-required="true"
                id="city"
                maxLength={200}
              />

              <CustomSelect
                label="State"
                placeholder="State"
                additionalText=" (Choose One)*"
                {...register('state', getFormFieldRules('state'))}
                options={stateOptions}
                error={errors.state?.message}
                aria-required="true"
                aria-label="Facility state"
                id="state"
                ref={stateSelectRef}
              />

              <ZipCodeInput
                label="ZIP Code"
                placeholder="ZIP Code*"
                {...register('zipCode', getFormFieldRules('zipCode'))}
                error={errors.zipCode?.message}
                aria-required="true"
                id="zipCode"
              />
              <TextInput
                label="Phone Number"
                placeholder="Phone Number*"
                {...register('phoneNumber', getFormFieldRules('phoneNumber'))}
                error={errors.phoneNumber?.message}
                aria-required="true"
                id="phoneNumber"
              />


            </div>
            <div className="grid grid-cols-1 mb-8"
              role="group"
              aria-labelledby={FORM_CONFIG.sections.clinical.id}
            >
              <p className="text-[14px] lg:text-p font-gt_walsheim_regular text-black/70 mt-8 mb-4 text-center">
                So we can best respond to your request, please describe how your representative can best assist you<br /> (ie, provide details regarding specific question(s) or indicate if you are interested in samples).
              </p>

              <TextArea
                label=""
                {...register('message', getFormFieldRules('message'))}
                error={errors.message?.message}
                id="message"
                maxLength={500}
              />
            </div>
            <div className="grid grid-cols-1 gap-4"
              role="group"
              aria-labelledby={FORM_CONFIG.sections.clinical.id}
            >
              <Checkbox
                label="Click here to opt-in to future communications from Pacira"
							  {...register('optInFutureCommunications', getFormFieldRules('optInFutureCommunications'))}
                checked={watch('optInFutureCommunications')}
                error={errors.optInFutureCommunications?.message}
                id="optInFutureCommunications"
              />
            </div>
            <div className="grid grid-cols-1 gap-6 mt-8 lg:mt-12">
              <span className="text-p_footnote text-black/70">
                *Required field
              </span>
              {hasClinicalSectionErrors && (
                <span className="text-p text-error">
                  *Please fill all the required fields
                </span>
              )}
            </div>
          </section>

          <div className="flex justify-center">
            <SubmitButton type="submit" aria-label="Submit form">
              Submit
            </SubmitButton>
          </div>
        </form>
      </FormProvider>
      {formIntroductionFooter && formIntroductionFooter.length > 0 && (
        <>
          {formIntroductionFooter.map((block, blockIndex) => (
            <Container
              key={`footer-block-${blockIndex}`}
              backgroundImage={block.backgroundImage}
              className={`${block.classOverlay?.classOverlay || ''}`}
            >
              {block.items?.map((item, index) => (
                <div key={`footer-item-${index}`}>
                  {item.__typename === 'ContentfulRichText' ? getText(item) : null}
                </div>
              ))}
            </Container>
          ))}
        </>
      )}
    </div>
  );
};

export default RequestARepresentativeForm;